'use client';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import ConfirmBanner from './confirm-banner';

interface UseConfirmDeleteBannerProps {
  title: string;
  children: ReactNode;
}

interface Ask {
  id: number;
  onConfirm: () => void;
  /** What asked, which takes focus back. Passed in: WebKit doesn't focus a clicked button. */
  opener: HTMLElement;
}

/**
 * `useConfirmDeleteModal`'s question asked inside the modal already open, as a band across its top
 * rather than a second modal stacked over it. Pass `banner` to the modal's `banner` and call
 * `confirmDelete` where the prompt would open. The modal holds the rest of itself inert while the
 * band shows, so nothing under the question changes before it's answered. A modal that closes with
 * the question open drops it.
 *
 * @example
 * ```tsx
 * const { banner, confirmDelete } = useConfirmDeleteBanner({
 *   title: t('Delete activity?'),
 *   children: t('This will remove the activity from the timeline.'),
 * });
 *
 * <ActionModal banner={banner} leftSection={
 *   <Button
 *     onClick={(event: MouseEvent<HTMLButtonElement>) =>
 *       confirmDelete({ onConfirm: remove, opener: event.currentTarget })
 *     }
 *   >
 *     {t('Remove')}
 *   </Button>
 * } … />
 * ```
 */
export function useConfirmDeleteBanner({ title, children }: UseConfirmDeleteBannerProps) {
  const [ask, setAsk] = useState<Ask | null>(null);
  const lastId = useRef(0);
  const drop = (id: number) => setAsk((current) => (current?.id === id ? null : current));
  // A tick later, once the modal around the band is no longer inert.
  const answer = ({ id, opener }: Ask) => {
    drop(id);
    setTimeout(() => opener.focus());
  };

  const banner = ask && (
    <ConfirmBanner
      onCancel={() => answer(ask)}
      onConfirm={() => {
        answer(ask);
        ask.onConfirm();
      }}
      onGone={() => drop(ask.id)}
      title={title}
    >
      {children}
    </ConfirmBanner>
  );

  const confirmDelete = ({ onConfirm, opener }: Pick<Ask, 'onConfirm' | 'opener'>) => {
    lastId.current += 1;
    setAsk({ id: lastId.current, onConfirm, opener });
  };

  return { banner, confirmDelete };
}
